import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { CheckCircle, ChevronDown } from "lucide-react";

interface CategoryDropdownQuestionProps {
  categories: Record<string, string[]>;
  currentAnswer: any;
  onAnswer: (answer: any) => void;
}

const CategoryDropdownQuestion = ({ categories, currentAnswer, onAnswer }: CategoryDropdownQuestionProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(
    currentAnswer?.category || null
  );
  const [categoryAnswers, setCategoryAnswers] = useState<string[]>(
    currentAnswer?.selections || []
  );

  const handleCategorySelect = (category: string) => {
    setSelectedCategory(category);
    setCategoryAnswers([]);
  };

  const handleSubselectionToggle = (subselection: string) => {
    const newAnswers = categoryAnswers.includes(subselection)
      ? categoryAnswers.filter(a => a !== subselection)
      : [...categoryAnswers, subselection];
    setCategoryAnswers(newAnswers);
    
    // Automatically update the answer when selections change
    if (selectedCategory) {
      onAnswer({
        category: selectedCategory,
        selections: newAnswers
      });
    }
  };

  return (
    <div className="space-y-6">
      {/* Category Selection */}
      {!selectedCategory && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Object.keys(categories).map((category) => (
            <Card
              key={category}
              className="p-4 cursor-pointer transition-all hover:shadow-md hover:bg-blue-50 border-2 hover:border-blue-200"
              onClick={() => handleCategorySelect(category)}
            >
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-gray-900">{category}</h3>
                <ChevronDown className="h-5 w-5 text-gray-400" />
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Subselection for chosen category */}
      {selectedCategory && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">{selectedCategory}</h3>
            <Button
              variant="ghost"
              onClick={() => {
                setSelectedCategory(null);
                setCategoryAnswers([]);
              }}
              className="text-blue-600"
            >
              Change Category
            </Button>
          </div>
          
          <div className="space-y-2">
            {categories[selectedCategory].map((subselection, index) => {
              const isSelected = categoryAnswers.includes(subselection);
              return (
                <Button
                  key={index}
                  variant={isSelected ? "default" : "outline"}
                  className={`w-full text-left justify-start p-3 h-auto whitespace-normal transition-all ${
                    isSelected 
                      ? "bg-blue-600 hover:bg-blue-700 text-white" 
                      : "hover:bg-blue-50 hover:border-blue-200"
                  }`}
                  onClick={() => handleSubselectionToggle(subselection)}
                >
                  {isSelected && <CheckCircle className="mr-2 h-4 w-4" />}
                  {subselection}
                </Button>
              );
            })}
          </div>

        </div>
      )}
    </div>
  );
};

export default CategoryDropdownQuestion;